import {useEffect, useMemo, useRef, useState} from "react";
import KeyValueCard from "./key-value-card.jsx";
import {PageSelector} from "../utils/alarms-actions-ui.jsx";
import {
  alarmDataKey,
  cortexDataKey,
  deconstructObjAsArr, eventsTimesKey,
  vehicleDataKey
} from "../utils/alarm-actions-utils.js";
import projectUtils from "../../../../utils/project-utils.js";
import HorizontalTableCard from "./horizontal-table-card.jsx";
import VerticalTableCard from "./vertical-table-card.jsx";
import CeleparDataTable from "./celepar-data-table.jsx";
import OccDataTable from "./occ-data-table.jsx";
import ImagePreparationProgress from "../../../../components/image-preparation-progress/image-preparation-progress.jsx";

const EMPTY_REPORT_DATA = [];
const PRINT_IMAGES_PER_LOAD = 20;
const PRINT_IMAGE_PLACEHOLDER = `${import.meta.env.VITE_API_URL}/public/assets/img/sentry.imgnotfound.large.748x480.png`;

const ReportPanel = ({data = EMPTY_REPORT_DATA, setCanPrint, imageLoadRevision}) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [preparedImageSources, setPreparedImageSources] = useState({});
  const [imageProgress, setImageProgress] = useState({loaded: 0, total: 0});
  const imageObjectUrlsRef = useRef({});
  const reportData = Array.isArray(data) ? data : EMPTY_REPORT_DATA;

  const revokeImageObjectUrls = () => {
    Object.values(imageObjectUrlsRef.current).forEach((url) => URL.revokeObjectURL(url));
    imageObjectUrlsRef.current = {};
  };

  useEffect(() => {
    let cancelled = false;
    let nextImageIndex = 0;
    let completedImages = 0;

    revokeImageObjectUrls();
    setPreparedImageSources({});
    setImageProgress({loaded: 0, total: reportData.length});
    setCanPrint?.(reportData.length === 0);

    const imageEntries = reportData.map((item, index) => ({
      key: `${index}-${item?.img ?? ""}`,
      url: projectUtils.translateImgPath(item?.img),
    }));

    const completeImage = (entry, source) => {
      if (cancelled) return;

      completedImages += 1;
      setPreparedImageSources((previous) => ({
        ...previous,
        [entry.key]: source,
      }));
      setImageProgress({loaded: completedImages, total: imageEntries.length});
    };

    const loadImageElement = (url) => new Promise((resolve, reject) => {
      const image = new Image();
      image.onload = () => resolve(url);
      image.onerror = reject;
      image.src = url;
    });

    const prepareImage = async (entry) => {
      if (!entry.url) {
        completeImage(entry, PRINT_IMAGE_PLACEHOLDER);
        return;
      }

      let objectUrl = null;

      try {
        const response = await fetch(entry.url, {
          cache: "force-cache",
          credentials: "include",
        });

        if (!response.ok) throw new Error("Imagem indisponível");

        const blob = await response.blob();
        if (cancelled) return;

        objectUrl = URL.createObjectURL(blob);
        imageObjectUrlsRef.current[entry.key] = objectUrl;
        await loadImageElement(objectUrl);
        completeImage(entry, objectUrl);
      } catch {
        if (objectUrl && imageObjectUrlsRef.current[entry.key] === objectUrl) {
          URL.revokeObjectURL(objectUrl);
          delete imageObjectUrlsRef.current[entry.key];
        }

        if (cancelled) return;

        try {
          await loadImageElement(entry.url);
          completeImage(entry, entry.url);
        } catch {
          completeImage(entry, PRINT_IMAGE_PLACEHOLDER);
        }
      }
    };

    const loadQueue = async () => {
      while (!cancelled) {
        const entry = imageEntries[nextImageIndex];
        nextImageIndex += 1;

        if (!entry) return;
        await prepareImage(entry);
      }
    };

    const workers = Array.from(
      {length: Math.min(PRINT_IMAGES_PER_LOAD, imageEntries.length)},
      loadQueue
    );

    Promise.all(workers).then(() => {
      if (cancelled) return;
      setCanPrint?.(true);
    });

    return () => {
      cancelled = true;
      revokeImageObjectUrls();
    };
  }, [reportData, imageLoadRevision, setCanPrint]);

  const visiblePages = useMemo(() => {
    const arr = [];
    let leftDiff = Math.max(0, currentPage - 3);

    while(leftDiff < currentPage){
      arr.push(leftDiff)
      leftDiff++;
    }

    for(let i = leftDiff; i <= (leftDiff + 3) && i < reportData.length; i++){
      arr.push(i)
    }

    return arr;
  }, [currentPage, reportData]);

  const currentPageInfo = useMemo(() => {
    return reportData[currentPage] ?? null;
  }, [currentPage, reportData]);

  function parseJson(value, fallback = null) {
    if (!value) return fallback;
    if (typeof value !== "string") return value ?? fallback;

    try {
      return JSON.parse(value) ?? fallback;
    } catch {
      return fallback;
    }
  }

  const renderReportContent = (info, index) => {
    const reportInfo = info ?? {};
    const occurrences = parseJson(reportInfo.occurrences, []);
    const cortex = parseJson(reportInfo.cortex);
    const celepar = parseJson(reportInfo.celepar);
    const imageKey = `${index}-${reportInfo.img ?? ""}`;
    const imageSource = preparedImageSources[imageKey] ?? PRINT_IMAGE_PLACEHOLDER;

    return (
      <div className="w-full print:break-after-page print:mb-8">
        <div className="flex w-full gap-2 mt-4 print:mt-0">
          <div className="flex-1 border border-[#505050] flex items-center print:break-inside-avoid">
            <img
              className="my-auto h-auto"
              src={imageSource}
              onError={(event) => {
                event.currentTarget.src = PRINT_IMAGE_PLACEHOLDER;
              }}
              loading="eager"
              decoding="sync"
              alt="Imagem do Evento"
            />
          </div>
          <div className="flex-1 print:break-inside-avoid">
            <KeyValueCard name="DADOS DO ALARME" items={deconstructObjAsArr(reportInfo, alarmDataKey)}/>
            <KeyValueCard className="mt-2" name="DADOS DO VEÍCULO" items={deconstructObjAsArr(reportInfo, vehicleDataKey)}/>
          </div>
        </div>

        {Array.isArray(occurrences) && occurrences.length > 0 &&
          <OccDataTable name="OCORRÊNCIAS" className="mt-2" data={occurrences}/>
        }

        {cortex &&
          <KeyValueCard className="mt-2" name="CÓRTEX" items={deconstructObjAsArr(cortex, cortexDataKey)}
                        dataCortex={cortex}/>
        }

        {celepar && <CeleparDataTable className="mt-2" name="SESP - PR" data={celepar}/>}
        <HorizontalTableCard className="mt-2" name="HORÁRIOS DO EVENTO" items={deconstructObjAsArr(reportInfo, eventsTimesKey)}/>
        <VerticalTableCard className="mt-2" name="AÇÕES COMPLEMENTARES" data={reportInfo}/>
      </div>
    );
  };

  return(
    <div>

      <div className="print:hidden">
        <ImagePreparationProgress
          loaded={imageProgress.loaded}
          total={imageProgress.total}
          className="mb-2 w-full"
        />
        <div className="flex justify-center items-center">
          {visiblePages.map(el =>
            <PageSelector index={el} key={el} selected={el === currentPage} onClick={() => setCurrentPage(el)}/>
          )}
        </div>
        { currentPageInfo && renderReportContent(currentPageInfo, currentPage) }
      </div>

      <div className="hidden print:block">
        {reportData.map((item, index) => (
          <div key={`print-page-${index}`}>
            {renderReportContent(item, index)}
          </div>
        ))}
      </div>
    </div>
  )
}

export default ReportPanel;